import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { FormErrors, InjectedFormProps } from 'redux-form';
import { FieldArray, getFormSyncErrors, getFormValues, reduxForm } from 'redux-form';

import type FieldWorkClassificationCodeListItemVM from 'ab-viewModels/fieldWorkClassificationCode/listItem';
import type OccupiedSlotsForWorkOrderVM from 'ab-viewModels/timeSheet/occupiedSlotsForWorkOrder.viewModel';
import type { TimeSheetVM } from 'ab-viewModels/timeSheet/timeSheet.viewModel';
import type { EquipmentListItemVM } from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';
import type { SubjobVM } from 'ab-viewModels/workRequest/jobUpsert.viewModel';

import type { TimeSheetsBulkUpdateRM } from 'ab-requestModels/timeSheet/timeSheetUpdate';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeUtils from '@acceligentllc/shared/utils/time';
import * as TimeSheetUtils from '@acceligentllc/shared/utils/timeSheet';
import { findOverlapsByVirtualId, getTimelineEntities } from '@acceligentllc/shared/utils/timeSheetEntry';

import type { FieldReportAccessRole } from '@acceligentllc/shared/enums/fieldReportAccessRole';
import { TimeSheetEntryWorkType } from '@acceligentllc/shared/enums/timeSheetEntryWorkType';
import type { WorkOrderReviewStatus, WorkOrderReviewLevel } from '@acceligentllc/shared/enums/workOrderReviewStatus';

import { DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL } from '@acceligentllc/shared/constants/value';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';
import type TimeZoneSourceType from 'ab-enums/timezoneSourceType.enum';

import * as ReduxFormKeys from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';
import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';
import TimezoneSourceBanner from 'af-components/TimezoneSourceBanner';

import * as TimeSheetActions from 'af-actions/timeSheet';

import type { TimeSheetAddedEntryWithType, TimeSheetEntryWithType, TimeSheetGapEntryWithType, TimeSheetOccupiedEntryWithType, TimesPerWorkType } from './formModel';
import TimeSheetEditFormModel, { TimeAllocationEntryFormModel, TimeSheetBulkEditFormModel, TimeSheetEntryFormModel, TimeSplitEntryFormModel } from './formModel';
import LoadingTab from './Loading';
import styles from './styles.module.scss';
import TimeSheetEditModalBody from './TimeSheetEditModalBody';
import type { OwnProps as ModalBodyProps } from './TimeSheetEditModalBody';
import { findEntriesTimeRange, offsetOccupiedSlots } from '../../helpers';
import type { TimeSheetAvailableActions } from '../../types';

const FORM_KEY = ReduxFormKeys.TIME_SHEET;

const getForm = getFormValues(FORM_KEY);

export enum BulkOrSingleEditModal {
	BULK = 'BULK',
	SINGLE = 'SINGLE'
}

interface TimeSheetWithPerDiem extends TimeSheetVM {
	perDiemInfo: {
		perDiemFlag: boolean;
		perDiemAmount: Nullable<string>;
	};
}
interface PerDiemInfo {
	perDiemFlag: boolean;
	perDiemAmount: Nullable<string>;
}
interface OwnProps {
	activeAccounts: { [accountId: number]: true; };
	close: () => void;
	save: (workOrderId: number, data: TimeSheetsBulkUpdateRM) => Promise<void>;
	canManagePerDiem: boolean;
	bulkOrSingleEditModal: BulkOrSingleEditModal;
	showModal: boolean;
	accessRole: Nullable<FieldReportAccessRole>;
	/** YYYY-MM-DD */
	dueDate: string;
	code: string;
	workOrderId: number;
	workRequestId: number;
	timeSheets: Nullable<TimeSheetWithPerDiem[]>;
	areAllTimeSheetsReadOnly: boolean;
	areFRsReadOnly: boolean;
	canSeeTimeAllocations: boolean;
	timeSheetsAvailableActions: TimeSheetAvailableActions[];
	/** Keeps track of work order / job / company time zone */
	timeZone: Nullable<string>;
	/** Keeps track of which time zone we're using in the form */
	timeZoneInUse: Nullable<string>;
	timezoneSourceType: Nullable<TimeZoneSourceType>;
	temporaryEmployeeAccounts: { [temporaryEmployeeId: number]: true; };
	isPaused: boolean;
	isSuperintendent: boolean;
	hasSIUnassignedPermission: boolean;
	workOrderReviewStatus: WorkOrderReviewStatus;
	workOrderReviewLevel: WorkOrderReviewLevel;
	classificationCodes: FieldWorkClassificationCodeListItemVM[];
	sections: Nullable<TimeSplitEntryEquipmentSections>[];
	isVirtual: boolean;
	isProjectOrSubjob: boolean;
	subjobsForAllocation: SubjobVM[];
	loadSubjobsForAllocation: () => Promise<void>;
}

type Props = OwnProps & InjectedFormProps<TimeSheetBulkEditFormModel> & ConnectedProps<typeof connector>;

type TimeSplitEntryEquipmentSections = {
	title: string;
	options: EquipmentListItemVM[];
};
type SingleTimeSheetSplitData = {
	id: number;
	indexOfNewEntry: Nullable<number>;
	occupiedSlots: Nullable<OccupiedSlotsForWorkOrderVM>;
	timeSheetEntries: TimeSheetEntryFormModel[];
	timeSheetEquipment: TimeSplitEntryFormModel[];
	timeAllocationEntries: TimeAllocationEntryFormModel[];
	accountId: number;
	userFullName: string;
	approvalStatus: TimeSheetVM['superintendentApprovalStatus'];
	employeeApprovalStatus: TimeSheetVM['employeeApprovalStatus'];
	perDiemInfo: PerDiemInfo;
};
type SingleTimeSheetSplitDataByFormId = {
	[tsId: string]: SingleTimeSheetSplitData;
};

const usePrevious = (value) => {
	const ref = React.useRef(undefined);
	React.useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

const round = (time: number, shouldRoundTimeIntervalsTo1Min: boolean): number =>
	shouldRoundTimeIntervalsTo1Min ? time : TimeUtils.roundTimeDurationToInterval(time, DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL);

const TimeSheetsEditModal: React.FC<Props> = (props) => {
	const {
		activeAccounts,
		close,
		save,
		showModal,
		workOrderId,
		timeSheets,
		areAllTimeSheetsReadOnly,
		areFRsReadOnly,
		timeZone,
		timeZoneInUse,
		timezoneSourceType,
		isPaused,
		workOrderReviewStatus,
		findWorkOrderTimeSheetAndSplitEntriesForAccount,
		findOccupiedSlots,
		formData,
		isManagementAllowedToEdit,
		isManagerOrAdmin,
		accountId,
		initialize,
		valid,
		submitting,
		handleSubmit,
		classificationCodes,
		sections,
		change,
		dueDate,
		bulkOrSingleEditModal,
		workRequestId,
		canSeeTimeAllocations,
		timeSheetsSyncErrors,
		isAccounting,
		isSuperintendent,
		isVirtual,
		temporaryEmployeeAccounts,
		canManagePerDiem,
		isProjectOrSubjob,
		loadSubjobsForAllocation,
		subjobsForAllocation,
		timeSheetsAvailableActions,
		shouldRoundTimeIntervalsTo1Min,
	} = props;

	const [showDiscardModalData, setShowDiscardModalData] = React.useState(false);
	const [storedFormData, setStoredFormData] = React.useState(null);
	const [isInEditMode, setIsInEditMode] = React.useState(false);
	const [timeSheetSplitData, setTimeSheetSplitData] = React.useState<SingleTimeSheetSplitDataByFormId>({});
	const [loadingResources, setLoadingResources] = React.useState(false);

	const isEditable = React.useMemo(() => (
		(!areAllTimeSheetsReadOnly || !areFRsReadOnly) && !isVirtual
	), [areAllTimeSheetsReadOnly, areFRsReadOnly, isVirtual]);

	const closeDiscardModal = React.useCallback(() => setShowDiscardModalData(false), []);

	const getOccupiedSlots = React.useCallback(async (employeeAccountId: number, entries: TimeSheetEntryFormModel[]) => {
		const { maxEndTime, minStartTime } = findEntriesTimeRange(entries, timeZoneInUse, timeZone);

		if (minStartTime && maxEndTime) {
			const occupiedSlotsResponse = await findOccupiedSlots(workOrderId, employeeAccountId, minStartTime, maxEndTime);
			return offsetOccupiedSlots(occupiedSlotsResponse, timeZoneInUse);
		} else {
			return null;
		}
	}, [findOccupiedSlots, timeZone, timeZoneInUse, workOrderId]);

	const loadOccupiedSlots = React.useCallback(async (timeSheetId: number, entries: TimeSheetEntryFormModel[]) => {
		const timeSheet = timeSheets?.find((ts) => ts.id === timeSheetId);

		if (!timeSheet || !entries) {
			return;
		}

		const occupiedSlots = await getOccupiedSlots(timeSheet.accountId, entries);

		setTimeSheetSplitData((tssData) => ({
			...tssData,
			['ts-' + timeSheetId]: {
				...tssData['ts-' + timeSheetId],
				occupiedSlots: occupiedSlots,
			},
		}));
	}, [getOccupiedSlots, timeSheets]);

	const loadTimeSheetAndSplitEntries = React.useCallback(async (timeSheetId: number) => {
		const timeSheet = timeSheets?.find((ts) => ts.id === timeSheetId);

		if (!timeSheet) {
			return;
		}

		setLoadingResources(true);

		const {
			timeSheetEntries,
			timeSplitEntries,
			timeAllocationEntries,
		} = await findWorkOrderTimeSheetAndSplitEntriesForAccount(workOrderId, timeSheet.accountId);
		const formSplitEntries = timeSplitEntries?.map(TimeSplitEntryFormModel.parseFromVM);
		const formSheetEntries = timeSheetEntries.map((_e) => TimeSheetEntryFormModel.parseFromVM(_e, timeZoneInUse));
		const formAllocationEntries = timeAllocationEntries?.map(TimeAllocationEntryFormModel.parseFromVM).flat();

		loadOccupiedSlots(timeSheet.id, formSheetEntries);
		if (canSeeTimeAllocations) {
			loadSubjobsForAllocation();
		}

		setTimeSheetSplitData((tssData) => ({
			...tssData,
			['ts-' + timeSheetId]: {
				...tssData['ts-' + timeSheetId],
				id: timeSheetId,
				accountId: timeSheet.accountId,
				userFullName: timeSheet.userFullName,
				approvalStatus: timeSheet.superintendentApprovalStatus,
				employeeApprovalStatus: timeSheet.employeeApprovalStatus,
				timeSheetEquipment: formSplitEntries,
				timeSheetEntries: formSheetEntries,
				timeAllocationEntries: formAllocationEntries,
				perDiemInfo: timeSheet.perDiemInfo,
				workShifts: timeSheet.workShifts,
			},
		}));
		setLoadingResources(false);

	}, [
		timeSheets,
		findWorkOrderTimeSheetAndSplitEntriesForAccount,
		workOrderId,
		loadOccupiedSlots,
		loadSubjobsForAllocation,
		timeZoneInUse,
		canSeeTimeAllocations,
	]);

	const initializeTotalTimesPerWorkType = React.useCallback((
		timeSheetEntriesWithTypes: (TimeSheetEntryWithType | TimeSheetGapEntryWithType | TimeSheetOccupiedEntryWithType | TimeSheetAddedEntryWithType)[]
	): TimesPerWorkType => {
		const timeSheetEntries = TimeSheetEditFormModel.mapEntriesWithTypesToEntries(
			TimeSheetEditFormModel.filterOutGapAndOccupiedEntries(timeSheetEntriesWithTypes)
		);

		const totalJobTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.JOB);
		const roundedTotalJobTime = round(totalJobTime, shouldRoundTimeIntervalsTo1Min);
		const totalBreakTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.BREAK);
		const roundedTotalBreakTime = round(totalBreakTime, shouldRoundTimeIntervalsTo1Min);
		const totalShopTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.SHOP);
		const roundedTotalShopTime = round(totalShopTime, shouldRoundTimeIntervalsTo1Min);
		const totalTravelTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.TRAVEL);
		const roundedTotalTravelTime = round(totalTravelTime, shouldRoundTimeIntervalsTo1Min);

		return {
			job: {
				roundedWorkTimeBefore: roundedTotalJobTime,
				roundedWorkTimeAfter: roundedTotalJobTime,
			},
			break: {
				roundedWorkTimeBefore: roundedTotalBreakTime,
				roundedWorkTimeAfter: roundedTotalBreakTime,
			},
			shop: {
				roundedWorkTimeBefore: roundedTotalShopTime,
				roundedWorkTimeAfter: roundedTotalShopTime,
			},
			travel: {
				roundedWorkTimeBefore: roundedTotalTravelTime,
				roundedWorkTimeAfter: roundedTotalTravelTime,
			},
		};
	}, [shouldRoundTimeIntervalsTo1Min]);

	const previousTimeSheets = usePrevious(timeSheets);
	React.useEffect(() => {
		if (!!timeSheets && timeSheets !== previousTimeSheets) {
			timeSheets.forEach((timeSheet) => {
				loadTimeSheetAndSplitEntries(timeSheet.id);
			});
		}
		if (!!timeSheets && !(formData?.timeSheets)?.length) {
			const bulkFormModel = new TimeSheetBulkEditFormModel();
			const bulkFormModelSheets: TimeSheetEditFormModel[] = [];
			let countOfInitialized = 0;
			Object.keys(timeSheetSplitData).forEach((tsKey) => {
				if (
					timeSheetSplitData?.[tsKey]?.timeSheetEntries
					&& timeSheetSplitData?.[tsKey]?.timeSheetEquipment
					&& timeSheetSplitData?.[tsKey]?.timeAllocationEntries
					&& timeSheetSplitData?.[tsKey]?.occupiedSlots !== undefined
				) {
					const overlaps = findOverlapsByVirtualId(timeSheetSplitData[tsKey].timeSheetEntries, timeSheetSplitData[tsKey].occupiedSlots?.slots);
					const hasOverlap = Object.keys(overlaps).some((_key) => (overlaps[_key].startTime || overlaps[_key].endTime || overlaps[_key].occupied));
					const timelineEntities = getTimelineEntities(
						timeSheetSplitData[tsKey].timeSheetEntries,
						timeSheetSplitData[tsKey].occupiedSlots?.slots,
						true
					);
					const timeSheetEntries = TimeSheetEditFormModel.mapTimelineEntitiesToTimeSheetEntriesList(timelineEntities);

					bulkFormModelSheets.push({
						id: timeSheetSplitData?.[tsKey].id,
						accountId: timeSheetSplitData?.[tsKey].accountId,
						userFullName: timeSheetSplitData?.[tsKey].userFullName,
						approvalStatus: timeSheetSplitData?.[tsKey].approvalStatus,
						employeeApprovalStatus: timeSheetSplitData?.[tsKey].employeeApprovalStatus,
						timeSheetEntries: timeSheetEntries,
						timeSplitEntries: timeSheetSplitData[tsKey].timeSheetEquipment,
						timeAllocationEntries: timeSheetSplitData?.[tsKey]?.timeAllocationEntries,
						timeSheetEntriesHaveChanged: false,
						timeSplitEntriesHaveChanged: false,
						timeAllocationEntriesHaveChanged: false,
						perDiemHasChanged: false,
						overlaps: overlaps,
						hasOverlap: hasOverlap,
						editingEntryIndex: null,
						editingEntryInitialEntry: null,
						timeSheetInEditIndex: null,
						workTimesBeforeAndAfter: initializeTotalTimesPerWorkType(timeSheetEntries),
						perDiemInfo: timeSheetSplitData?.[tsKey].perDiemInfo,
					});
					countOfInitialized += 1;
				}

			});
			if (countOfInitialized === timeSheets.length) {
				bulkFormModel.timeSheets = bulkFormModelSheets;
				initialize(bulkFormModel);
			}
		} else if (!timeSheets && timeSheets !== previousTimeSheets) {
			initialize(new TimeSheetBulkEditFormModel());
		}
	}, [
		timeSheets,
		formData,
		initialize,
		loadOccupiedSlots,
		previousTimeSheets,
		timeSheetSplitData,
		loadTimeSheetAndSplitEntries,
		timeZoneInUse,
		shouldRoundTimeIntervalsTo1Min,
		initializeTotalTimesPerWorkType,
	]);

	const cancel = React.useCallback(() => {
		close();
		setStoredFormData(null);
		setShowDiscardModalData(false);
		setIsInEditMode(false);
		setTimeSheetSplitData({});
	}, [close]);

	const promptCancel = React.useCallback(() => {
		if (!timeSheets) {
			return;
		}

		if (TimeSheetBulkEditFormModel.hasUnsavedChanges(formData)) {
			setShowDiscardModalData(true);
			setIsInEditMode(false);
		} else {
			cancel();
		}
	}, [cancel, formData, timeSheets]);

	const saveChanges = React.useCallback(async () => {
		if (!timeSheets) {
			throw new Error('timeSheet are required in this context');
		}

		const requestModel = TimeSheetBulkEditFormModel.toRequestModel(formData, timeSheets, timeZoneInUse);
		await save(workOrderId, requestModel);

		cancel();
	}, [cancel, formData, save, timeSheets, timeZoneInUse, workOrderId]);

	const isAllowedToEditTimeSheet = React.useCallback((_accountId: number) => {
		if (isVirtual) {
			return false;
		}
		return TimeSheetUtils.isUserAllowedToEdit(
			_accountId === accountId,
			isManagerOrAdmin,
			isSuperintendent,
			isManagementAllowedToEdit
		);
	}, [accountId, isManagementAllowedToEdit, isManagerOrAdmin, isSuperintendent, isVirtual]);

	const renderSingleTimeSheetWithEquipment = React.useCallback(() => {
		if (!formData || !timeSheets) {
			return null;
		}
		if (loadingResources
			|| !classificationCodes
			|| !sections) {
			return (<LoadingTab key={'loading'} />);
		}

		return (
			<FieldArray<ModalBodyProps>
				activeAccounts={activeAccounts}
				areFRsReadOnly={areFRsReadOnly || isVirtual}
				canManagePerDiem={canManagePerDiem && (isManagerOrAdmin || isSuperintendent || isAccounting)}
				canSeeTimeAllocations={canSeeTimeAllocations}
				change={change}
				classificationCodes={classificationCodes}
				component={TimeSheetEditModalBody}
				dueDate={dueDate}
				getOccupiedSlots={getOccupiedSlots}
				isAllowedToEditTimeSheet={isAllowedToEditTimeSheet}
				isEditable={isEditable}
				isSuperintendent={isSuperintendent}
				isVirtual={isVirtual}
				isWorkOrderPaused={isPaused}
				name="timeSheets"
				rerenderOnEveryChange={true}
				sections={sections}
				shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min}
				subJobs={subjobsForAllocation}
				temporaryEmployeeAccounts={temporaryEmployeeAccounts}
				timeSheetsAvailableActions={timeSheetsAvailableActions}
				timeSheetsSyncErrors={timeSheetsSyncErrors}
				timeZoneInUse={timeZoneInUse}
				workOrderReviewStatus={workOrderReviewStatus}
				workRequestId={workRequestId}
				workShifts={timeSheets.flatMap((_ts) => _ts.workShifts)}
			/>
		);
	}, [
		activeAccounts,
		formData,
		timeSheets,
		loadingResources,
		classificationCodes,
		sections,
		areFRsReadOnly,
		isVirtual,
		canSeeTimeAllocations,
		canManagePerDiem,
		change,
		dueDate,
		getOccupiedSlots,
		isAllowedToEditTimeSheet,
		isPaused,
		isEditable,
		isManagerOrAdmin,
		isSuperintendent,
		isAccounting,
		subjobsForAllocation,
		timeSheetsSyncErrors,
		timeZoneInUse,
		temporaryEmployeeAccounts,
		workOrderReviewStatus,
		workRequestId,
		timeSheetsAvailableActions,
		shouldRoundTimeIntervalsTo1Min,
	]);

	const hasEquipmentOverJobError = React.useMemo(() => {
		const _timeSheets = formData?.timeSheets ?? [];
		if (!_timeSheets.length) {
			return false;
		}

		return _timeSheets.some((ts) => {
			if (isProjectOrSubjob) {
				// sum JOB allocations per allocatedWorkRequestId
				const jobByWr = (ts.timeAllocationEntries ?? []).reduce(
					(acc, allocation) => {
						if (allocation.workType === TimeSheetEntryWorkType.JOB) {
							const wrId = allocation.allocatedWorkRequestId;
							const dur = allocation.time;
							acc[wrId] = (acc[wrId] ?? 0) + dur;
						}
						return acc;
					},
					{} as Record<number, number>
				);

				// sum Equipment minutes per allocatedWorkRequestId
				const equipmentByWr = (ts.timeSplitEntries ?? []).reduce(
					(acc, e) => {
						const wrId = e.allocatedWorkRequestId;
						const dur = e.duration;
						acc[wrId] = (acc[wrId] ?? 0) + dur;
						return acc;
					},
					{} as Record<number, number>
				);

				// if any sub-job has equipment time > rounded job time -> hard error
				const wrIds = new Set<number>([
					...Object.keys(jobByWr).map(Number),
					...Object.keys(equipmentByWr).map(Number),
				]);

				for (const wrId of wrIds) {
					const jobTime = round(jobByWr[wrId] ?? 0, shouldRoundTimeIntervalsTo1Min ?? false);
					const equipmentTime = equipmentByWr[wrId] ?? 0;
					if (equipmentTime > jobTime) {
						return true; // trigger .some() early exit
					}
				}
				return false;
			} else {
				// not project/subjob: validate against time sheet entries
				const timeSheetEntries = TimeSheetEditFormModel.mapEntriesWithTypesToEntries(
					TimeSheetEditFormModel.filterOutGapAndOccupiedEntries(ts.timeSheetEntries)
				);

				const totalJobTime = TimeSheetEditFormModel.totalWorkTypeTime(
					timeSheetEntries,
					TimeSheetEntryWorkType.JOB
				);
				const roundedTotalJobTime = round(totalJobTime, shouldRoundTimeIntervalsTo1Min);
				const equipmentTotal = (ts.timeSplitEntries ?? []).reduce<number>(
					(acc, e) => acc + e.duration,
					0
				);

				return equipmentTotal > roundedTotalJobTime;
			}
		});
	}, [formData?.timeSheets, isProjectOrSubjob, shouldRoundTimeIntervalsTo1Min]);

	const hasWorkTypeTimeAllocationError = React.useMemo(() => {
		if (!formData?.timeSheets?.length) {
			return false;
		}

		return formData.timeSheets.some((ts) => {

			const timeSheetEntries = TimeSheetEditFormModel.mapEntriesWithTypesToEntries(
				TimeSheetEditFormModel.filterOutGapAndOccupiedEntries(ts.timeSheetEntries)
			);

			const totalJobTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.JOB);
			const roundedTotalJobTime = round(totalJobTime, shouldRoundTimeIntervalsTo1Min);
			const totalBreakTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.BREAK);
			const roundedTotalBreakTime = round(totalBreakTime, shouldRoundTimeIntervalsTo1Min);
			const totalShopTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.SHOP);
			const roundedTotalShopTime = round(totalShopTime, shouldRoundTimeIntervalsTo1Min);
			const totalTravelTime = TimeSheetEditFormModel.totalWorkTypeTime(timeSheetEntries, TimeSheetEntryWorkType.TRAVEL);
			const roundedTotalTravelTime = round(totalTravelTime, shouldRoundTimeIntervalsTo1Min);
			const {
				jobAllocationsTime,
				breakAllocationsTime,
				shopAllocationsTime,
				travelAllocationsTime,
			} = TimeAllocationEntryFormModel.mapEntriesToTimesPerWorkType(ts.timeAllocationEntries);

			return roundedTotalJobTime !== jobAllocationsTime
				|| roundedTotalBreakTime !== breakAllocationsTime
				|| roundedTotalShopTime !== shopAllocationsTime
				|| roundedTotalTravelTime !== travelAllocationsTime;
		});
	}, [formData?.timeSheets, shouldRoundTimeIntervalsTo1Min]);

	if (!timeSheets) {
		return null;
	}

	const hasNoAddedTimeSheetEntriesInProgress = TimeSheetBulkEditFormModel.hasNoAddedTimeSheetEntriesInProgress(formData);

	const isSaveChangesEnabled = valid
		&& TimeSheetBulkEditFormModel.hasUnsavedChanges(formData)
		&& hasNoAddedTimeSheetEntriesInProgress
		&& !isInEditMode
		&& !hasEquipmentOverJobError
		&& !hasWorkTypeTimeAllocationError;

	return (
		<>
			<CustomModal
				className={styles['time-sheet-bulk-edit-modal']}
				closeModal={promptCancel}
				showModal={showModal && !!timeSheets && !storedFormData && !showDiscardModalData}
				size="lg"
			>
				<CustomModal.Header
					className={`${!hasNoAddedTimeSheetEntriesInProgress ? styles['time-sheet-bulk-edit-modal__title-opaque'] : ''}`}
					closeModal={promptCancel}
					title={bulkOrSingleEditModal === BulkOrSingleEditModal.BULK ? (isEditable ? 'Time Sheets Bulk Edit' : 'Time Sheets Bulk Preview') : (isEditable ? 'Edit Time Sheet' : 'Preview Time Sheet')}
				/>
				<TimezoneSourceBanner
					showLocalTimeZone={true}
					timezoneInUse={timeZoneInUse}
					timezoneSourceType={timezoneSourceType}
				/>
				<CustomModal.Body className={styles['time-sheet-bulk-edit-modal']}>
					{renderSingleTimeSheetWithEquipment()}
				</CustomModal.Body>
				<CustomModal.Footer>
					<Button
						customStyleClass={!hasNoAddedTimeSheetEntriesInProgress ? styles['time-sheet-bulk-edit-modal__footer-opaque'] : undefined}
						label="Cancel"
						onClick={promptCancel}
						style="secondary"
					/>
					{isEditable &&
						<SubmitButton
							className={`${!hasNoAddedTimeSheetEntriesInProgress ? styles['time-sheet-bulk-edit-modal__footer-opaque'] : ''}`}
							disabled={!isSaveChangesEnabled}
							label="Save Changes"
							onClick={handleSubmit(saveChanges)}
							reduxFormSubmitting={submitting}
							variant="primary"
						/>
					}
				</CustomModal.Footer>
			</CustomModal>
			<ConfirmationModal
				body="Canceling without saving will discard all changes made to the Time Sheets. Do you want to continue?"
				closeModal={closeDiscardModal}
				closeText="Cancel"
				confirmAction={cancel}
				confirmText="Discard"
				modalStyle="danger"
				showModal={showDiscardModalData}
				title="Discard Time Sheet Changes"
			/>
		</>
	);
};

const formConnector = reduxForm<TimeSheetBulkEditFormModel, Props>({
	form: FORM_KEY,
	validate: TimeSheetBulkEditFormModel.validate,
});

const getErrors = getFormSyncErrors(FORM_KEY);
export type EntryValidationErrors = TimeSheetBulkEditFormModel;

const mapStateToProps = (state: RootState, props: OwnProps) => {
	const formData = getForm(state) as TimeSheetBulkEditFormModel;
	const {
		user: {
			userData,
			companyData,
		},
		company: {
			company,
		},
	} = state;
	const { dueDate } = props;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { timeSheets: timeSheetsSyncErrors } = getErrors(state) as FormErrors<EntryValidationErrors, string>;

	const { role } = userData;
	const { accountId, isCompanyAdmin, permissions } = companyData;
	const isAccounting = isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE.ACCOUNTING, permissions, isCompanyAdmin, role);

	return {
		formData,
		accountId,
		isAccounting,
		isManagerOrAdmin: isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE, permissions, isCompanyAdmin, role),
		isManagementAllowedToEdit: company?.isFRManageAllowedToEditTimeSheet ?? false,
		shouldRoundTimeIntervalsTo1Min: company?.timeRoundingPolicyFrom
			? TimeSheetUtils.shouldEnableFeatureFromDate({ enableFeatureFrom: company.timeRoundingPolicyFrom, workOrderDueDate: dueDate })
			: false,
		timeSheetsSyncErrors: timeSheetsSyncErrors as FormErrors<EntryValidationErrors, string>,
	};
};

const mapDispatchToProps = () => {
	return {
		findOccupiedSlots: TimeSheetActions.findOccupiedSlotsWithInterval,
		findWorkOrderTimeSheetAndSplitEntriesForAccount: TimeSheetActions.findWorkOrderTimeSheetAndSplitEntriesForAccount,
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	formConnector,
	connector
);

export default enhance(TimeSheetsEditModal);
